import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import {
  listApplications,
  exportApplicationsCsvUrl,
} from "../lib/applications";
import { Download } from "lucide-react";
import StatusPill from "../components/StatusPill";
import { buildFilterBar } from "../lib/statusFilters";
import ListTable, { ListRow } from "../components/ListTable";

/* Shared grid template so the header and every row stay column-aligned. */
const ROW_GRID = "minmax(0, 1.8fr) minmax(0, 1.4fr) 180px 160px";
const COL_GAP = 18;

const FILTERS = buildFilterBar([
  "pending_verification",
  "more_info_requested",
  "approved",
  "rejected",
]);

export default function AdminApplications() {
  const { user } = useRole();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setData(null);
    listApplications(user.id, filter)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setErr(e));
    return () => {
      cancelled = true;
    };
  }, [user?.id, filter]);

  const counts = data?.counts || {
    pending_verification: 0,
    more_info_requested: 0,
    approved: 0,
    rejected: 0,
  };

  return (
    <section
      className="animate-fade-in-up"
      data-testid="admin-applications-page"
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          Platform Admin · Architect verification
        </span>
      </div>

      <div className="mt-3 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1
            style={{
              fontSize: "clamp(28px, 4vw, 36px)",
              fontWeight: 600,
              lineHeight: 1.1,
            }}
          >
            Application Queue.
          </h1>
          <p
            className="mt-2 tabular-nums"
            style={{ color: "#6C6B85", fontSize: 15 }}
            data-testid="queue-summary"
          >
            <strong style={{ color: "#1B1F4B", fontWeight: 800 }}>
              {counts.pending_verification}
            </strong>{" "}
            <em style={{ fontFamily: '"Newsreader", Georgia, serif' }}>
              awaiting review
            </em>{" "}
            ·{" "}
            <strong style={{ color: "#1B1F4B", fontWeight: 800 }}>
              {counts.more_info_requested}
            </strong>{" "}
            <em style={{ fontFamily: '"Newsreader", Georgia, serif' }}>
              more info pending
            </em>{" "}
            ·{" "}
            <strong style={{ color: "#1B1F4B", fontWeight: 800 }}>
              {counts.approved}
            </strong>{" "}
            <em style={{ fontFamily: '"Newsreader", Georgia, serif' }}>
              approved
            </em>{" "}
            ·{" "}
            <strong style={{ color: "#1B1F4B", fontWeight: 800 }}>
              {counts.rejected}
            </strong>{" "}
            <em style={{ fontFamily: '"Newsreader", Georgia, serif' }}>
              rejected
            </em>
            .
          </p>
        </div>
        <a
          href={exportApplicationsCsvUrl(user.id)}
          className="wn-btn wn-btn-tertiary"
          data-testid="export-csv-btn"
        >
          <Download size={14} /> Export CSV
        </a>
      </div>

      <div
        className="mt-6 flex flex-wrap gap-2"
        data-testid="queue-filters"
      >
        {FILTERS.map((f) => {
          const isOn = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`queue-filter-${f.key}`}
              style={{
                padding: "7px 14px",
                borderRadius: 999,
                border: `1px solid ${isOn ? "#1B1F4B" : "#D9D8E4"}`,
                backgroundColor: isOn ? "#1B1F4B" : "#FBFAFD",
                color: isOn ? "#FBFAFD" : "#272636",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 24 }} />

      {err && (
        <div
          className="mt-8"
          style={{ color: "#A23B2E", fontSize: 14 }}
          data-testid="queue-error"
        >
          Could not load the queue. Please verify your admin session.
        </div>
      )}

      {!err && (
        <ListTable
          testId="queue"
          columns={[
            { label: "Applicant" },
            { label: "Service areas" },
            { label: "Status" },
            { label: "Submitted", align: "right" },
          ]}
          gridTemplate={ROW_GRID}
          gap={COL_GAP}
          items={data?.items}
          loadingHeight={64}
          emptyTitle="No applications in this state right now."
          emptyBody="Try a different filter, or wait for new submissions to arrive."
          renderRow={(app, _i, isLast) => (
            <QueueRow
              key={app.id}
              app={app}
              isLast={isLast}
              onOpen={() => navigate(`/admin/applications/${app.id}`)}
            />
          )}
        />
      )}
    </section>
  );
}

function QueueRow({ app, onOpen, isLast }) {
  return (
    <ListRow
      as="button"
      type="button"
      onClick={onOpen}
      gridTemplate={ROW_GRID}
      gap={COL_GAP}
      isLast={isLast}
      style={{
        width: "100%",
        cursor: "pointer",
        background: "transparent",
        border: "none",
        textAlign: "left",
      }}
      data-testid={`queue-row-${app.id}`}
    >
      {/* Applicant column — name, firm, license */}
      <div className="min-w-0">
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 16,
            color: "#1B1F4B",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {app.contact?.name}
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {app.firm}
          {app.license_no ? (
            <span
              className="tabular-nums"
              style={{
                fontStyle: "normal",
                color: "#3A5563",
                fontSize: 11.5,
                fontWeight: 600,
                marginLeft: 8,
              }}
            >
              · {app.license_no}
            </span>
          ) : null}
        </div>
      </div>

      {/* Service areas */}
      <div className="min-w-0 flex flex-wrap" style={{ gap: 4 }}>
        {(app.service_areas || []).map((s) => (
          <span
            key={s}
            style={{
              padding: "2px 8px",
              borderRadius: 999,
              backgroundColor: "rgba(91,97,150,0.10)",
              color: "#1B1F4B",
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: "0.03em",
              whiteSpace: "nowrap",
            }}
          >
            {s}
          </span>
        ))}
      </div>

      {/* Status — fixed-width column so the date never drifts */}
      <div className="flex">
        <StatusPill
          value={app.state}
          testId={`state-badge-${app.state}`}
        />
      </div>

      {/* Submitted date — right-aligned tabular */}
      <span
        className="tabular-nums"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 12,
          whiteSpace: "nowrap",
          textAlign: "right",
        }}
      >
        {fmtDate(app.submitted_at)}
      </span>
    </ListRow>
  );
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
